import { injectIntl } from "react-intl";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TableHeader from "src/components/TableHeader";
import {
  OFFERS_TABLE_TITLE_WIDTHS,
  OFFERS_TABLE_TITLES,
  OFFERS_TABLE_TITLE_SORT,
} from "src/helpers/Constants";
import { OfferTableSortType } from "src/helpers/Enums";
import Pagination from "src/components/Pagination";
import apiService from "src/services/api.service";

const SORT_FIELD_TO_COLUMN_INDEX = {
  name: 0,
  totalRevenue: 1,
  score: 4,
  emailScore: 5,
  smsScore: 6,
};

const OffersTable = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOfferIds, setSelectedOfferIds] = useState(() => new Set());
  const [isDeleteMenuOpen, setIsDeleteMenuOpen] = useState(false);
  const deleteMenuRef = useRef(null);
  const [openRowDeleteMenuId, setOpenRowDeleteMenuId] = useState(null);
  const rowDeleteMenuRef = useRef(null);

  const toggleOfferSelection = (offerId) => {
    setSelectedOfferIds((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  };

  const activeSortColumnIndex = SORT_FIELD_TO_COLUMN_INDEX[props.sortField];
  const isAscending = props.sortOrder === "asc";
  const offersSortState =
    OFFERS_TABLE_TITLE_SORT?.map((item, index) => ({
      ...item,
      active: activeSortColumnIndex === index ? isAscending : false,
    })) ?? OFFERS_TABLE_TITLE_SORT;
  const getCreatives = (offerId) => {
    return props.creatives?.filter(
      (creative) => creative.offer_id?._id === offerId
    );
  };

  const formatRevenue = (amount) => {
    if (amount == null || isNaN(amount)) {
      return "-";
    }
    let formatted = Number(amount).toFixed(2);
    formatted = formatted.replace(/\.00$/, "");
    return `$${formatted}`;
  };

  const formatCount = (count, label) => {
    if (!count) {
      return "-";
    }
    return `${count} ${label}`;
  };

  const getOfferRankColor = (offer) => {
    switch (offer.status_color) {
      case "green":
        return "#53A852";
      case "orange":
        return "#F0970B";
      case "red":
        return "#DD2E2E";
      default:
        return "#DD2E2E";
    }
  };

  const updateOffer = (offer) => {
    apiService
      .updateOffer({
        ...offer,
        is_enabled: !offer?.is_enabled,
        linkType: offer?.link_type,
        linkId: offer?.link_id,
      })
      .then(() => props.closeModal());
  };

  const selectionCount = selectedOfferIds.size;
  const hasSelection = selectionCount > 0;

  const clearSelection = () => {
    setSelectedOfferIds(new Set());
    setIsDeleteMenuOpen(false);
  };

  const handleDeleteSelected = (deleteFromEverflow = false) => {
    const ids = Array.from(selectedOfferIds);
    if (!ids.length) {
      return;
    }
    props.onRequestDeleteOffers?.(ids, {
      deleteFromEverflow,
      clearSelectionOnSuccess: true,
    });
    setIsDeleteMenuOpen(false);
  };

  useEffect(() => {
    if (!hasSelection) {
      setIsDeleteMenuOpen(false);
    }
  }, [hasSelection]);

  useEffect(() => {
    if (props.deleteNonce > 0) {
      setSelectedOfferIds(new Set());
      setIsDeleteMenuOpen(false);
    }
  }, [props.deleteNonce]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        deleteMenuRef.current &&
        !deleteMenuRef.current.contains(event.target)
      ) {
        setIsDeleteMenuOpen(false);
      }
      if (
        rowDeleteMenuRef.current &&
        !rowDeleteMenuRef.current.contains(event.target)
      ) {
        setOpenRowDeleteMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={"w-full flex flex-col justify-between h-full"}>
      <div className={"w-full flex flex-col gap-3"}>
        {hasSelection ? (
          <div
            className="flex w-full flex-[1_0_0] self-stretch items-center justify-between rounded-lg border border-[#F2EBFB] bg-[#F9F5FF] py-2 px-[10px]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <div
              className="text-[12px] font-semibold leading-6 text-[#7E50EA]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {selectionCount} {selectionCount === 1 ? "offer" : "offers"}{" "}
              selected
            </div>
            <div className="flex items-center gap-2">
              <div className="relative" ref={deleteMenuRef}>
                <button
                  type="button"
                  className="inline-flex h-[30px] items-center justify-center gap-4 rounded-md border border-[#E5E7F0] bg-white px-3 text-[12px] font-semibold text-[#DD2E2E]"
                  style={{
                    boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)",
                  }}
                  onClick={() => setIsDeleteMenuOpen((prev) => !prev)}
                >
                  <div className="flex items-center gap-1">
                    <img
                      alt=""
                      src="/media/icons/deleteIcon.svg"
                      className="h-[14px] w-[14px] shrink-0"
                    />
                    Delete Selected
                  </div>
                  <img
                    alt=""
                    src="/media/icons/chevron-down-red.svg"
                    className={`h-[14px] w-[14px] shrink-0 transition-transform duration-150 ${
                      isDeleteMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isDeleteMenuOpen && (
                  <div
                    className="absolute right-0 top-[36px] z-20 flex w-[270px] flex-col items-start gap-1 rounded-lg border border-[#E5E7F0] bg-white p-2"
                    style={{ boxShadow: "0 1px 6px 0 rgba(23, 23, 23, 0.10)" }}
                  >
                    <button
                      type="button"
                      className="flex self-stretch items-center gap-[6px] px-2 py-2 pl-[10px] text-[12px] font-medium leading-[14px] text-[#DD2E2E]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                      onClick={() => handleDeleteSelected(false)}
                    >
                      <img
                        alt=""
                        src="/media/icons/deleteIcon.svg"
                        className="h-[18px] w-[18px] shrink-0"
                      />
                      Delete from Schedsmart only
                    </button>
                    <div className="h-px self-stretch rounded-lg bg-[#E5E7F0]" />
                    <button
                      type="button"
                      className="flex self-stretch items-center gap-[6px] px-2 py-2 pl-[10px] text-[12px] font-medium leading-[14px] text-[#DD2E2E]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                      onClick={() => handleDeleteSelected(true)}
                    >
                      <img
                        alt=""
                        src="/media/icons/deleteIcon.svg"
                        className="h-[18px] w-[18px] shrink-0"
                      />
                      Delete from Schedsmart &amp; Everflow
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="inline-flex h-[30px] items-center justify-center gap-1 rounded-md border border-[#E5E7F0] bg-white px-3 text-[12px] font-semibold leading-normal text-[#15151F]"
                style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
                onClick={clearSelection}
              >
                Clear Selection
              </button>
            </div>
          </div>
        ) : (
          <TableHeader
            titles={OFFERS_TABLE_TITLES}
            widths={OFFERS_TABLE_TITLE_WIDTHS}
            sort={offersSortState}
            sortType={OfferTableSortType}
            setList={props.setList}
            list={props.list}
            textAlignments={[null, "right", null, null, null, null]}
            showIconForColumns={[null, true, null, null, null, null]}
            onSortChange={props.onSortChange}
          />
        )}
        <div
          className="TableWrapper"
          style={{ overflow: "visible" }}
        >
          <table className="Table">
            <tbody>
              {props.list?.map((offer, index) => {
                const isOfferSelected = selectedOfferIds.has(offer._id);
                return (
                  <tr
                    key={index}
                    className="border-b h-[60px] hover:bg-[#F6F6F9]"
                  >
                    <td
                      className="py-3 pl-[6px] text-[12px] leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[0] }}
                    >
                      <div className="flex items-center gap-[6px]">
                        <label className="inline-flex shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={isOfferSelected}
                            onChange={() => toggleOfferSelection(offer._id)}
                            aria-label={`Select offer ${offer.name ?? ""}`}
                          />
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border ${
                              isOfferSelected
                                ? "border-[#7F56D9] bg-[#F9F5FF]"
                                : "border-[#D0D5DD] bg-white"
                            }`}
                          >
                            {isOfferSelected && (
                              <img
                                alt="Logo"
                                src="/media/icons/check.svg"
                                className="w-3 h-3"
                              />
                            )}
                          </span>
                        </label>
                        <div
                          className="w-[3px] h-8 rounded-full"
                          style={{
                            backgroundColor: getOfferRankColor(offer),
                          }}
                        />
                        <div>
                          {offer.name}
                          {offer.link_id && (
                            <img
                              alt="Logo"
                              src="/media/icons/link-hyperlink-icon.svg"
                              className="w-[14px] h-[14px]"
                            />
                          )}
                        </div>
                      </div>
                    </td>
                    <td
                      className="py-3 px-3 border-r text-[12px] text-end leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[1] }}
                    >
                      <div
                        className="inline-flex items-center h-[18px] px-[6px] rounded-[34px] bg-[#53A852] text-white text-[11px] font-semibold"
                        style={{ fontFamily: "Inter" }}
                      >
                        {formatRevenue(offer.totalRevenue)}
                      </div>
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[2] }}
                    >
                      <div className="flex items-center gap-1">
                        <img
                          alt={"Logo"}
                          src={"/media/icons/stars-01.svg"}
                          className={"w-[14px] h-[14px]"}
                        />
                        {formatCount(
                          getCreatives(offer._id)?.length,
                          "Creative"
                        )}
                      </div>
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[3] }}
                    >
                      {formatCount(offer.list_count, "Lists") !== "-" ? (
                        <div className="flex items-center gap-1">
                          <img
                            alt={"Logo"}
                            src={"/media/icons/rows.svg"}
                            className={"w-[14px] h-[14px]"}
                          />
                          {formatCount(offer.list_count, "Lists")}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[4] }}
                    >
                      {offer.score != null ? offer.score?.toFixed(2) : "-"}
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[5] }}
                    >
                      {offer.emailScore != null
                        ? offer.emailScore?.toFixed(2)
                        : "-"}
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[6] }}
                    >
                      {offer.smsScore != null
                        ? offer.smsScore?.toFixed(2)
                        : "-"}
                    </td>
                    <td
                      className="py-3 px-4 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: OFFERS_TABLE_TITLE_WIDTHS[7] }}
                    >
                      <div className="relative flex justify-center items-center gap-0">
                        <button
                          className="OffersNewCreativeButton whitespace-nowrap"
                          onClick={() =>
                            navigate("/offers/details", {
                              state: {
                                offerId: offer._id,
                                offersReturnSearch: location.search ?? "",
                              },
                            })
                          }
                        >
                          <img
                            alt={"Logo"}
                            src={"/media/icons/eye.svg"}
                            className={"w-[14px] h-[14px]"}
                          />
                          View Details
                        </button>
                        <button
                          className="OffersEditButton"
                          onClick={() =>
                            updateOffer({
                              ...offer,
                              linkType: offer?.link_type,
                              linkId: offer?.link_id,
                            })
                          }
                        >
                          <img
                            alt={"Logo"}
                            src={
                              offer.is_enabled
                                ? "/media/icons/pauseCircle.svg"
                                : "/media/icons/playCircle.svg"
                            }
                            className={"w-4 h-4"}
                          />
                        </button>
                        <button
                          className="OffersEditButton"
                          onClick={() => props.handleOpenOfferEdit(offer)}
                        >
                          <img
                            alt={"Logo"}
                            src={"/media/icons/editIcon.svg"}
                            className={"w-4 h-4"}
                          />
                        </button>
                        <button
                          className="OffersDeleteButton"
                          onClick={() =>
                            setOpenRowDeleteMenuId((prev) =>
                              prev === offer._id ? null : offer._id
                            )
                          }
                        >
                          <img
                            alt={"Logo"}
                            src={"/media/icons/deleteIcon.svg"}
                            className={"w-4 h-4"}
                          />
                        </button>
                        {openRowDeleteMenuId === offer._id && (
                          <div
                            ref={rowDeleteMenuRef}
                            className="absolute right-0 top-[36px] z-20 flex w-[270px] flex-col items-start gap-1 rounded-lg border border-[#E5E7F0] bg-white p-2"
                            style={{
                              boxShadow: "0 1px 6px 0 rgba(23, 23, 23, 0.10)",
                            }}
                          >
                            <button
                              type="button"
                              className="flex self-stretch items-center gap-[6px] px-2 py-2 pl-[10px] text-[12px] font-medium leading-[14px] text-[#DD2E2E]"
                              style={{ fontFamily: "Inter, sans-serif" }}
                              onClick={() => {
                                props.onRequestDeleteOffers?.([offer._id], {
                                  deleteFromEverflow: false,
                                });
                                setOpenRowDeleteMenuId(null);
                              }}
                            >
                              <img
                                alt=""
                                src="/media/icons/deleteIcon.svg"
                                className="h-[18px] w-[18px] shrink-0"
                              />
                              Delete from Schedsmart only
                            </button>
                            <div className="h-px self-stretch rounded-lg bg-[#E5E7F0]" />
                            <button
                              type="button"
                              className="flex self-stretch items-center gap-[6px] px-2 py-2 pl-[10px] text-[12px] font-medium leading-[14px] text-[#DD2E2E]"
                              style={{ fontFamily: "Inter, sans-serif" }}
                              onClick={() => {
                                props.onRequestDeleteOffers?.([offer._id], {
                                  deleteFromEverflow: true,
                                });
                                setOpenRowDeleteMenuId(null);
                              }}
                            >
                              <img
                                alt=""
                                src="/media/icons/deleteIcon.svg"
                                className="h-[18px] w-[18px] shrink-0"
                              />
                              Delete from Schedsmart &amp; Everflow
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination
        currentPage={props.currentPage}
        totalPages={props.totalPages}
        totalCount={props.totalCount}
        pageSize={props.pageSize}
        onPageChange={props.onPageChange}
      />
    </div>
  );
};

export default injectIntl(OffersTable);
